import { useState, useEffect } from 'react';
import { X, Phone, Mail, MapPin, Send, User, MessageSquare, Clock, Globe, Star, ChevronRight, ExternalLink } from 'lucide-react';
import emailjs from 'emailjs-com';

const ContactPopup = ({ showContactPopup, setShowContactPopup, darkMode }) => {
  const [activeTab, setActiveTab] = useState('contact');
  const [emailForm, setEmailForm] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    priority: 'normal'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [errors, setErrors] = useState({});

  // Animation states
  const [isVisible, setIsVisible] = useState(false);
  const [slideAnimation, setSlideAnimation] = useState('');

  useEffect(() => {
    if (showContactPopup) {
      setIsVisible(true);
      setTimeout(() => setSlideAnimation('animate-in'), 50);
    } else {
      setSlideAnimation('animate-out');
      setTimeout(() => setIsVisible(false), 300);
    }
  }, [showContactPopup]);

  const handleClose = () => {
    setSlideAnimation('animate-out');
    setTimeout(() => {
      setShowContactPopup(false);
      setActiveTab('contact');
      setSubmitStatus(null);
    }, 300);
  };

  const validateForm = () => {
    const newErrors = {};
    if (!emailForm.name.trim()) newErrors.name = 'Name is required';
    if (!emailForm.email.trim()) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(emailForm.email)) newErrors.email = 'Email is invalid';
    if (!emailForm.subject.trim()) newErrors.subject = 'Subject is required';
    if (!emailForm.message.trim()) newErrors.message = 'Message is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  

const handleSubmit = async (e) => {
  e.preventDefault();
  if (!validateForm()) return;

  setIsSubmitting(true);

  const templateParams = {
    name: emailForm.name,
    email: emailForm.email,
    subject: emailForm.subject,
    message: emailForm.message,
    priority: emailForm.priority
  };

  try {
    await emailjs.send(
      'service_vk6eiym',     // e.g. service_xxxxx
      'template_f1u8j9f',    // e.g. template_yyyyy
      templateParams,
      'P9DB1kB37lbSYsdbr'      // e.g. iKxxyZc4abcdef123
    );

    setIsSubmitting(false);
    setSubmitStatus('success');
    setEmailForm({ name: '', email: '', subject: '', message: '', priority: 'normal' });

    setTimeout(() => {
      handleClose();
    }, 2000);

  } catch (error) {
    console.error('EmailJS Error:', error);
    setSubmitStatus('error');
    setIsSubmitting(false);
  }
};

  const contactMethods = [
    {
      icon: Phone,
      label: 'Phone Support',
      value: '+1 (234) 567-8900',
      description: '24/7 Concierge Service',
      action: 'tel:+1234567890',
      color: 'text-blue-500',
      bgColor: 'bg-blue-50 dark:bg-blue-900/20'
    },
    {
      icon: Mail,
      label: 'Email Support',
      value: 'concierge@grandhotel.com',
      description: 'Response within 2 hours',
      action: () => setActiveTab('email'),
      color: 'text-amber-500',
      bgColor: 'bg-amber-50 dark:bg-amber-900/20'
    },
    {
      icon: MapPin,
      label: 'Visit Us',
      value: '123 Luxury Avenue',
      description: 'Beverly Hills, CA 90210',
      action: 'https://maps.google.com',
      color: 'text-emerald-500',
      bgColor: 'bg-emerald-50 dark:bg-emerald-900/20'
    }
  ];

  const quickActions = [
    { label: 'Book a Room', icon: Star },
    { label: 'Restaurant Reservations', icon: MessageSquare },
    { label: 'Spa Appointments', icon: Clock },
    { label: 'Event Planning', icon: Globe }
  ];

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div 
        className={`relative w-full max-w-4xl transition-all duration-300 ease-out ${
          slideAnimation === 'animate-in' ? 'scale-100 opacity-100 translate-y-0' : 
          slideAnimation === 'animate-out' ? 'scale-95 opacity-0 translate-y-4' : 
          'scale-95 opacity-0 translate-y-4'
        }`}
      >
        <div className={`rounded-2xl shadow-2xl overflow-hidden ${
          darkMode ? 'bg-gray-900 text-white' : 'bg-white text-gray-900'
        }`}>
          {/* Header */}
          <div className="relative bg-gradient-to-r from-amber-500 to-amber-600 p-6 text-white">
            <div className="absolute inset-0 bg-black/10"></div>
            <div className="relative flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-serif font-bold">Get in Touch</h2>
                <p className="text-amber-100 mt-1">We're here to make your stay extraordinary</p>
              </div>
              <button
                onClick={handleClose}
                className="p-2 hover:bg-white/20 rounded-full transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            
            {/* Tab Navigation */}
            <div className="mt-6 flex space-x-1 bg-white/20 rounded-lg p-1">
              <button
                onClick={() => setActiveTab('contact')}
                className={`px-4 py-2 rounded-md font-medium transition-all ${
                  activeTab === 'contact' 
                    ? 'bg-white text-amber-600 shadow-sm' 
                    : 'text-white/80 hover:text-white hover:bg-white/10'
                }`}
              >
                Contact Info
              </button>
              <button
                onClick={() => setActiveTab('email')}
                className={`px-4 py-2 rounded-md font-medium transition-all ${
                  activeTab === 'email' 
                    ? 'bg-white text-amber-600 shadow-sm' 
                    : 'text-white/80 hover:text-white hover:bg-white/10'
                }`}
              >
                Send Message
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 max-h-[60vh] overflow-y-auto">
            {activeTab === 'contact' && (
              <div className="space-y-6">
                {/* Contact Methods */}
                <div className="grid md:grid-cols-3 gap-4">
                  {contactMethods.map((method, index) => (
                    <div
                      key={index}
                      className={`p-4 rounded-xl border transition-all cursor-pointer hover:scale-105 hover:shadow-lg ${
                        darkMode 
                          ? 'border-gray-700 hover:border-gray-600 bg-gray-800/50' 
                          : 'border-gray-200 hover:border-gray-300 bg-gray-50/50'
                      } ${method.bgColor}`}
                      onClick={() => {
                        if (typeof method.action === 'function') {
                          method.action();
                        } else if (method.action.startsWith('http')) {
                          window.open(method.action, '_blank');
                        } else {
                          window.open(method.action);
                        }
                      }}
                    >
                      <div className="flex items-start space-x-3">
                        <div className={`p-2 rounded-lg ${method.bgColor}`}>
                          <method.icon className={`h-5 w-5 ${method.color}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-sm mb-1">{method.label}</h3>
                          <p className={`text-sm font-medium ${method.color}`}>{method.value}</p>
                          <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            {method.description}
                          </p>
                        </div>
                        <ChevronRight className={`h-4 w-4 ${darkMode ? 'text-gray-400' : 'text-gray-400'}`} />
                      </div>
                    </div>
                  ))}
                </div>

                {/* Quick Actions */}
                <div>
                  <h3 className="font-semibold mb-3">Quick Actions</h3>
                  <div className="grid md:grid-cols-2 gap-3">
                    {quickActions.map((action, index) => (
                      <button
                        key={index}
                        className={`p-3 rounded-lg border text-left transition-all hover:scale-[1.02] hover:shadow-md ${
                          darkMode 
                            ? 'border-gray-700 hover:border-amber-500 bg-gray-800/30' 
                            : 'border-gray-200 hover:border-amber-500 bg-gray-50/30'
                        }`}
                      >
                        <div className="flex items-center space-x-3">
                          <action.icon className="h-5 w-5 text-amber-500" />
                          <span className="font-medium">{action.label}</span>
                          <ExternalLink className="h-4 w-4 ml-auto text-gray-400" />
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Operating Hours */}
                <div className={`p-4 rounded-xl ${darkMode ? 'bg-gray-800/50' : 'bg-gray-50'}`}>
                  <h3 className="font-semibold mb-3">Operating Hours</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>Concierge Service</span>
                      <span className="text-amber-500 font-medium">24/7</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Front Desk</span>
                      <span>24/7</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Guest Services</span>
                      <span>6:00 AM - 12:00 AM</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'email' && (
              <div className="max-w-2xl mx-auto">
                {submitStatus === 'success' ? (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Send className="h-8 w-8 text-green-600" />
                    </div>
                    <h3 className="text-xl font-semibold mb-2">Message Sent Successfully!</h3>
                    <p className={`${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      We'll get back to you within 2 hours.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-5">
                    {/* Form Fields */}
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-2">
                          Full Name *
                        </label>
                        <div className="relative">
                          <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                          <input
                            type="text"
                            value={emailForm.name}
                            onChange={(e) => setEmailForm({...emailForm, name: e.target.value})}
                            className={`w-full pl-10 pr-4 py-3 rounded-lg border transition-all ${
                              errors.name 
                                ? 'border-red-500 focus:border-red-500' 
                                : 'border-gray-300 focus:border-amber-500'
                            } ${
                              darkMode 
                                ? 'bg-gray-800/50 text-white placeholder-gray-400' 
                                : 'bg-white text-gray-900 placeholder-gray-500'
                            } focus:ring-0 focus:outline-none`}
                            placeholder="Enter your full name"
                          />
                        </div>
                        {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-2">
                          Email Address *
                        </label>
                        <div className="relative">
                          <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                          <input
                            type="email"
                            value={emailForm.email}
                            onChange={(e) => setEmailForm({...emailForm, email: e.target.value})}
                            className={`w-full pl-10 pr-4 py-3 rounded-lg border transition-all ${
                              errors.email 
                                ? 'border-red-500 focus:border-red-500' 
                                : 'border-gray-300 focus:border-amber-500'
                            } ${
                              darkMode 
                                ? 'bg-gray-800/50 text-white placeholder-gray-400' 
                                : 'bg-white text-gray-900 placeholder-gray-500'
                            } focus:ring-0 focus:outline-none`}
                            placeholder="Enter your email"
                          />
                        </div>
                        {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">
                        Subject *
                      </label>
                      <input
                        type="text"
                        value={emailForm.subject}
                        onChange={(e) => setEmailForm({...emailForm, subject: e.target.value})}
                        className={`w-full px-4 py-3 rounded-lg border transition-all ${
                          errors.subject 
                            ? 'border-red-500 focus:border-red-500' 
                            : 'border-gray-300 focus:border-amber-500'
                        } ${
                          darkMode 
                            ? 'bg-gray-800/50 text-white placeholder-gray-400' 
                            : 'bg-white text-gray-900 placeholder-gray-500'
                        } focus:ring-0 focus:outline-none`}
                        placeholder="What can we help you with?"
                      />
                      {errors.subject && <p className="text-red-500 text-sm mt-1">{errors.subject}</p>}
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">
                        Priority Level
                      </label>
                      <select
                        value={emailForm.priority}
                        onChange={(e) => setEmailForm({...emailForm, priority: e.target.value})}
                        className={`w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-amber-500 ${
                          darkMode 
                            ? 'bg-gray-800/50 text-white' 
                            : 'bg-white text-gray-900'
                        } focus:ring-0 focus:outline-none`}
                      >
                        <option value="low">Low - General Inquiry</option>
                        <option value="normal">Normal - Standard Request</option>
                        <option value="high">High - Urgent Matter</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">
                        Message *
                      </label>
                      <div className="relative">
                        <MessageSquare className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                        <textarea
                          value={emailForm.message}
                          onChange={(e) => setEmailForm({...emailForm, message: e.target.value})}
                          rows={5}
                          className={`w-full pl-10 pr-4 py-3 rounded-lg border transition-all resize-none ${
                            errors.message 
                              ? 'border-red-500 focus:border-red-500' 
                              : 'border-gray-300 focus:border-amber-500'
                          } ${
                            darkMode 
                              ? 'bg-gray-800/50 text-white placeholder-gray-400' 
                              : 'bg-white text-gray-900 placeholder-gray-500'
                          } focus:ring-0 focus:outline-none`}
                          placeholder="Please describe your inquiry in detail..."
                        />
                      </div>
                      {errors.message && <p className="text-red-500 text-sm mt-1">{errors.message}</p>}
                      <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        {emailForm.message.length}/500 characters
                      </p>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex space-x-3 pt-4">
                      <button
                        type="button"
                        onClick={() => setActiveTab('contact')}
                        className={`px-6 py-3 rounded-lg font-medium transition-all ${
                          darkMode 
                            ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' 
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        Back to Contact
                      </button>
                      <button
                        type="button"
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                        className="flex-1 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white px-6 py-3 rounded-lg font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                      >
                        {isSubmitting ? (
                          <>
                            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                            <span>Sending...</span>
                          </>
                        ) : (
                          <>
                            <Send className="h-5 w-5" />
                            <span>Send Message</span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPopup;